import { useAtomValue } from "jotai";
import { useMemo } from "react";

import { CardTypeEnum } from "@sorcery/db";

import { avatarJotai, decklistJotai } from "~/state/deckJotai";
import type { CardType, VariantType } from "~/types/card";
import { DecklistSection } from "./DecklistSection";
import { Maybeboard } from "./Maybeboard";
import { Sideboard } from "./Sideboard";

type Props = {
  onCardHover?: (squiggle: { card: CardType; variant: VariantType }) => void;
  size: "sm" | "lg";
};

export const Decklist = ({ onCardHover, size }: Props) => {
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const decklist = useAtomValue(decklistJotai)!;
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const avatar = useAtomValue(avatarJotai)!;

  const sections = useMemo(() => {
    return [
      CardTypeEnum.Avatar,
      CardTypeEnum.Aura,
      CardTypeEnum.Artifact,
      CardTypeEnum.Minion,
      CardTypeEnum.Magic,
      CardTypeEnum.Site,
    ]
      .filter(
        (t) =>
          avatar.card.guardian?.type === t ||
          decklist.filter((dc) => dc.card.guardian?.type === t).length,
      )
      .map((t) => {
        const cards =
          t === CardTypeEnum.Avatar
            ? [avatar]
            : decklist.filter((dc) => dc.card.guardian?.type === t);
        return <DecklistSection key={t} title={t} cards={cards} onCardHover={onCardHover} />;
      });
  }, [avatar, decklist, onCardHover]);

  // dont count avatars as it takes up so little vertical space
  const sectionCount = sections.length - 1;

  return (
    <>
      <div
        className={`mb-4 columns-1 sm:mb-8 ${
          size === "sm"
            ? "md:gap-4"
            : `md:gap-6 ${sectionCount > 1 && "xl:columns-2"} ${sectionCount > 2 && "2xl:columns-3"}`
        }`}
      >
        {sections}
      </div>
      <Sideboard onCardHover={onCardHover} />
      <Maybeboard onCardHover={onCardHover} />
    </>
  );
};
